import { useEffect, useState } from 'react';

/**
 * Three-tier responsive breakpoint hook.
 *
 * Mobile-first thresholds:
 *
 *   mobile   < 640px   (single column, stacked layout, no leaderboard sidebar)
 *   tablet   640–1023  (two columns, narrower sidebars, touch-sized buttons)
 *   desktop  ≥ 1024    (current full layout)
 *
 * Backed by ``matchMedia`` so it costs zero on every render and updates
 * synchronously when the viewport crosses a boundary (browser resize,
 * device rotation).  SSR-safe: returns ``'desktop'`` on the server.
 */

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export const MOBILE_MAX = 639;
export const TABLET_MAX = 1023;

/**
 * Pure classifier -- exported separately so it can be unit-tested
 * without a DOM.  Boundaries are inclusive on the lower side and
 * exclusive on the upper, matching the CSS ``max-width`` semantics.
 */
export function breakpointForWidth(width: number): Breakpoint {
  if (width <= MOBILE_MAX) return 'mobile';
  if (width <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

function readBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') return 'desktop';
  return breakpointForWidth(window.innerWidth);
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(readBreakpoint);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mobile = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const tablet = window.matchMedia(
      `(min-width: ${MOBILE_MAX + 1}px) and (max-width: ${TABLET_MAX}px)`,
    );
    const update = (): void => setBp(readBreakpoint());
    mobile.addEventListener('change', update);
    tablet.addEventListener('change', update);
    // Catch the initial mismatch (SSR vs client / very first render).
    update();
    return () => {
      mobile.removeEventListener('change', update);
      tablet.removeEventListener('change', update);
    };
  }, []);

  return bp;
}

/** Convenience: returns ``true`` on mobile only. */
export function useIsMobile(): boolean {
  return useBreakpoint() === 'mobile';
}

/** Convenience: returns ``true`` on mobile or tablet. */
export function useIsCompact(): boolean {
  const bp = useBreakpoint();
  return bp === 'mobile' || bp === 'tablet';
}
